import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-search-eye-line",
    title: "先溯源，再盘点",
    desc: "每一期内容都要求标注信息来源，优先采用多方交叉验证后的一致口径，不让未经核实的瓜轻易上榜。",
  },
  {
    icon: "ri-shield-check-line",
    title: "客观中立",
    desc: "不给当事人预设立场，尽量还原事件的全貌与时间线，把判断留给读者，而不是替读者抢先站队。",
  },
  {
    icon: "ri-timer-flash-line",
    title: "理性吃瓜",
    desc: "对仍在发酵、证据不完整的话题，我们明确标注'仍在发酵'，倡导大众在全面了解后再作判断。",
  },
  {
    icon: "ri-heart-3-line",
    title: "守住边界",
    desc: "关注公共价值与真实关切，避免传播恶意中伤与无底线八卦，让吃瓜这件事保持该有的分寸感。",
  },
];

const testimonials = [
  {
    quote: "每年年初必刷他们的年度盘点，瓜王排得是真有说服力，节奏感也舒服，一次看完全不累。",
    name: "年年追瓜的读者 · 上海",
  },
  {
    quote: "最喜欢月度黑马的设定，很多好内容都是在这里提前知道的，再也不怕漏掉潜力剧了。",
    name: "综艺深度爱好者 · 广州",
  },
  {
    quote: "最难得的是他们讲瓜讲得客观，反转之前从来不带节奏，这点在吃瓜圈太难得了。",
    name: "理性吃瓜群众 · 成都",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 吃瓜榜单品牌介绍与内容理念",
    description:
      "了解吃瓜榜单：关于我们专注把热点做成榜单、把吃瓜做成时间线，坚持先溯源后盘点与理性吃瓜原则，提供年度盘点与月度总结服务。",
    keywords: "关于我们,吃瓜榜单,内容原则,品牌介绍",
    canonicalPath: "/about",
    ogType: "website",
    jsonLdId: "about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们 - 吃瓜榜单",
      description: "吃瓜榜单的品牌介绍、发展理念与内容原则",
      url: "https://chiguabangdan.example/about",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://chiguabangdan.example/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://chiguabangdan.example/about" },
        ],
      },
    },
  });

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 pt-24 md:px-6">
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">关于我们</span>
        </nav>

        <div className="mt-8 max-w-3xl">
          <h1 className="text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
            我们是谁，<br className="hidden md:block" />以及为什么要认真吃瓜
          </h1>
          <p className="mt-6 text-base leading-8 text-foreground-700">
            「吃瓜榜单」是一站式盘点型内容站，专注把分散在互联网各个角落的热点，用年度盘点与月度总结的方式结构化呈现。
            我们相信，真正值得被记住的瓜不该被淹没在信息流里，也不该被消费得只剩八卦的皮毛。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            { num: "120+", label: "累计产出榜单" },
            { num: "36期", label: "月度总结" },
            { num: "8年", label: "持续吃瓜沉淀" },
            { num: "50+", label: "覆盖热点栏目" },
          ].map((s) => (
            <div key={s.label} className="rounded-lg bg-background-100/70 p-6 text-center ring-1 ring-background-200/60">
              <p className="font-display text-3xl text-primary-600 md:text-4xl">{s.num}</p>
              <p className="mt-2 text-sm text-foreground-600">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 发展理念与定位 */}
      <section className="bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-primary-600">我们的定位</p>
              <h2 className="text-2xl font-black text-foreground-950 md:text-3xl">做热点的"时光机"与"导航仪"</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                大众最常遇到的两件事：一是错过热点，二是在信息爆炸里吃不明白。吃瓜榜单想做两件事——把时间轴倒回去，替你把错过的瓜逐月逐年级地补回来；
                再把热度排成座次，让你一眼看清当下该追什么、什么真正值得深入看。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                我们不满足于转述情绪，而希望帮你建立对一件热点的完整认知：起因是什么、中间如何发酵、各方如何回应、最终如何收场。
                吃瓜不只是一种消遣，也可以是一次次更高质量的公共参与。
              </p>
              <Link
                to="/annual"
                className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-semibold text-background-50 hover:bg-primary-600"
              >
                从年度盘点开始
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {principles.map((p, i) => (
                <div key={p.title} className="flex gap-4 rounded-lg bg-background-50 p-5 ring-1 ring-background-200/70">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${p.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                      <span className="font-display text-primary-500">0{i + 1}</span>
                      {p.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-foreground-600">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">读者怎么说</p>
          <h2 className="mt-2 text-2xl font-black text-foreground-950 md:text-3xl">大家一起认真吃瓜</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-lg bg-background-100/70 p-6 ring-1 ring-background-200/60">
              <i className="ri-double-quotes-l text-3xl text-accent-400" aria-hidden="true"></i>
              <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">{t.quote}</blockquote>
              <figcaption className="mt-4 flex items-center gap-2 text-sm text-foreground-500">
                <span className="h-px w-5 bg-primary-400" aria-hidden="true"></span>
                {t.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-16 md:px-6">
        <div className="flex flex-col gap-5 rounded-lg bg-primary-100/60 p-7 md:flex-row md:items-center md:justify-between md:p-9">
          <div>
            <h2 className="text-xl font-black text-foreground-950 md:text-2xl">一起把瓜讲得更清楚？</h2>
            <p className="mt-2 text-sm leading-6 text-foreground-700">
              无论是投稿线索、内容建议还是商务合作，欢迎随时联系我们。我们很愿意倾听你的声音。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href="mailto:hello@chiguabangdan.example"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-mail-line" aria-hidden="true"></i>
              hello@chiguabangdan.example
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 font-semibold text-primary-700 ring-1 ring-primary-200 hover:bg-accent-100"
            >
              在线联系
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}